import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Label } from './label.tsx'

/**
 * Field = mono label + control + hint/error (prototype `.field`).
 * <Field label="Email" htmlFor="email" error={fields.email.errors?.[0]}><Input id="email" … /></Field>
 * Pass `aria-invalid` on the control yourself (or use the error prop + `invalid` helper).
 */
export function Field({
	label,
	htmlFor,
	hint,
	error,
	className,
	children,
	labelVariant,
}: {
	label?: React.ReactNode
	htmlFor?: string
	hint?: React.ReactNode
	error?: React.ReactNode
	className?: string
	children: React.ReactNode
	labelVariant?: 'mono' | 'plain'
}) {
	const errorId = htmlFor ? `${htmlFor}-error` : undefined
	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{label ? (
				<Label htmlFor={htmlFor} variant={labelVariant}>
					{label}
				</Label>
			) : null}
			{children}
			{error ? (
				<p id={errorId} className="text-[13px] text-bad">
					{error}
				</p>
			) : hint ? (
				<p className="text-[13px] text-stone">{hint}</p>
			) : null}
		</div>
	)
}

/** Form-level error banner (prototype `.form-error`). */
export function FormError({ children, className }: { children?: React.ReactNode; className?: string }) {
	if (!children) return null
	return (
		<div role="alert" className={cn('rounded bg-bad-soft px-4 py-3 text-sm text-bad', className)}>
			{children}
		</div>
	)
}

/** Two-up field row that collapses on mobile. */
export function FieldRow({ children, className }: { children: React.ReactNode; className?: string }) {
	return <div className={cn('grid gap-4 sm:grid-cols-2', className)}>{children}</div>
}
